import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { NuevaReservaDialog, type ReservaFormValues } from "@/components/gestion/reservas/NuevaReservaDialog";
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  isSameDay,
  isWeekend,
  parseISO,
  startOfDay,
} from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, Trash2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BloqueosPanel } from "@/components/gestion/BloqueosPanel";

type Hab = { id: string; nombre: string; orden: number; capacidad?: number };
type Reserva = {
  id: string; habitacion_id: string; fecha_entrada: string; fecha_salida: string;
  nombre_huesped: string; email_huesped: string; telefono_huesped: string;
  num_huespedes: number; precio_total: number; senal_pagada: number;
  estado: string; notas: string;
};
type Bloqueo = { id: string; habitacion_id: string; fecha_inicio: string; fecha_fin: string; motivo: string; origen: string };

const RANGOS = { "14": 14, "30": 30, "90": 90 } as const;
type RangoKey = keyof typeof RANGOS;

function ymd(d: Date) { return format(d, "yyyy-MM-dd"); }
function parseYmd(s: string) { return startOfDay(parseISO(s)); }

export const Route = createFileRoute("/_authenticated/gestion/calendario")({
  component: CalendarioPage,
  validateSearch: (s: Record<string, unknown>) => {
    const tab = (s.tab as string) === "bloqueos" ? "bloqueos" : "calendario";
    const rango: RangoKey = s.rango === "14" || s.rango === "90" ? (s.rango as RangoKey) : "30";
    const desde = typeof s.desde === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.desde) ? s.desde : ymd(startOfDay(new Date()));
    return { tab, rango, desde };
  },
});

function CalendarioPage() {
  const { tab, rango, desde } = Route.useSearch();
  const navigate = Route.useNavigate();

  const rangeDays = RANGOS[rango as RangoKey];
  const inicio = parseYmd(desde);
  const fin = addDays(inicio, rangeDays - 1);

  const [habs, setHabs] = useState<Hab[]>([]);
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [bloqueos, setBloqueos] = useState<Bloqueo[]>([]);
  const [editReserva, setEditReserva] = useState<Partial<ReservaFormValues> | null>(null);
  const [reservaOpen, setReservaOpen] = useState(false);
  const [editBloqueo, setEditBloqueo] = useState<Partial<Bloqueo> | null>(null);
  const [detailReserva, setDetailReserva] = useState<Reserva | null>(null);
  const [detailBloqueo, setDetailBloqueo] = useState<Bloqueo | null>(null);

  async function load() {
    const desdeStr = ymd(addDays(inicio, -7));
    const hastaStr = ymd(addDays(fin, 7));
    const [{ data: h }, { data: r }, { data: b }] = await Promise.all([
      supabase.from("habitaciones").select("id, nombre, orden, capacidad").order("orden"),
      supabase.from("reservas").select("*")
        .neq("estado", "cancelada")
        .lte("fecha_entrada", hastaStr).gte("fecha_salida", desdeStr),
      supabase.from("bloqueos").select("*")
        .lte("fecha_inicio", hastaStr).gte("fecha_fin", desdeStr),
    ]);
    setHabs(h ?? []);
    setReservas((r ?? []) as Reserva[]);
    setBloqueos((b ?? []) as Bloqueo[]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [desde, rango]);

  const days = useMemo(() => eachDayOfInterval({ start: inicio, end: fin }), [desde, rango]);
  const today = startOfDay(new Date());
  const colW = rangeDays <= 14 ? 56 : rangeDays <= 30 ? 40 : 24;

  function shift(deltaDays: number) {
    navigate({ search: (p: any) => ({ ...p, desde: ymd(addDays(parseYmd(p.desde), deltaDays)) }) });
  }
  function goToday() {
    navigate({ search: (p: any) => ({ ...p, desde: ymd(startOfDay(new Date())) }) });
  }
  function setRango(r: RangoKey) {
    navigate({ search: (p: any) => ({ ...p, rango: r }) });
  }

  // Reservation bars per room: render full span entrada..salida (salida exclusive)
  function reservasDe(habId: string) {
    return reservas
      .filter((r) => r.habitacion_id === habId)
      .map((r) => {
        const start = parseYmd(r.fecha_entrada);
        const end = parseYmd(r.fecha_salida); // exclusive
        const colStart = Math.max(0, differenceInCalendarDays(start, inicio));
        const colEnd = Math.min(rangeDays, differenceInCalendarDays(end, inicio));
        const span = colEnd - colStart;
        if (span <= 0) return null;
        return { r, colStart, span, clippedLeft: start < inicio, clippedRight: end > addDays(fin, 1) };
      })
      .filter(Boolean) as { r: Reserva; colStart: number; span: number; clippedLeft: boolean; clippedRight: boolean }[];
  }
  function bloqueosDe(habId: string) {
    return bloqueos
      .filter((b) => b.habitacion_id === habId)
      .map((b) => {
        const start = parseYmd(b.fecha_inicio);
        const end = addDays(parseYmd(b.fecha_fin), 1); // inclusive end → exclusive
        const colStart = Math.max(0, differenceInCalendarDays(start, inicio));
        const colEnd = Math.min(rangeDays, differenceInCalendarDays(end, inicio));
        const span = colEnd - colStart;
        if (span <= 0) return null;
        return { b, colStart, span };
      })
      .filter(Boolean) as { b: Bloqueo; colStart: number; span: number }[];
  }

  // Casa completa: día ocupado si TODAS las habitaciones tienen reserva/bloqueo ese día
  function ocupacionCasa(day: Date): "full" | "partial" | "empty" {
    if (habs.length === 0) return "empty";
    let ocupadas = 0;
    for (const h of habs) {
      const hit =
        reservas.some((r) => r.habitacion_id === h.id && day >= parseYmd(r.fecha_entrada) && day < parseYmd(r.fecha_salida)) ||
        bloqueos.some((b) => b.habitacion_id === h.id && day >= parseYmd(b.fecha_inicio) && day <= parseYmd(b.fecha_fin));
      if (hit) ocupadas++;
    }
    if (ocupadas === 0) return "empty";
    if (ocupadas === habs.length) return "full";
    return "partial";
  }

  function openNuevaReserva(habId?: string, fecha?: Date) {
    setEditReserva({
      habitacion_id: habId ?? habs[0]?.id,
      fecha_entrada: fecha ? ymd(fecha) : "",
      fecha_salida: fecha ? ymd(addDays(fecha, 1)) : "",
      canal: "telefono",
    });
    setReservaOpen(true);
  }
  function openEditReserva(r: Reserva) {
    setEditReserva({
      id: r.id,
      habitacion_id: r.habitacion_id,
      cliente_id: (r as any).cliente_id ?? null,
      nombre_huesped: r.nombre_huesped,
      email_huesped: r.email_huesped,
      telefono_huesped: r.telefono_huesped,
      fecha_entrada: r.fecha_entrada,
      fecha_salida: r.fecha_salida,
      num_huespedes: r.num_huespedes,
      precio_total: Number(r.precio_total),
      senal_pagada: Number(r.senal_pagada),
      estado: r.estado,
      notas: r.notas,
      canal: (r as any).canal ?? "directo",
      metodo_pago: (r as any).metodo_pago ?? "pendiente",
    });
    setReservaOpen(true);
  }
  function openNuevoBloqueo(habId?: string, fecha?: Date) {
    setEditBloqueo({
      habitacion_id: habId ?? habs[0]?.id,
      fecha_inicio: fecha ? ymd(fecha) : "",
      fecha_fin: fecha ? ymd(fecha) : "",
      motivo: "", origen: "manual",
    });
  }
  async function saveBloqueo() {
    if (!editBloqueo) return;
    const b = editBloqueo;
    if (!b.habitacion_id || !b.fecha_inicio || !b.fecha_fin) return toast.error("Completa habitación y fechas");
    if (b.fecha_fin < b.fecha_inicio) return toast.error("La fecha de fin debe ser ≥ a la de inicio");
    const { error } = await supabase.from("bloqueos").insert({
      habitacion_id: b.habitacion_id,
      fecha_inicio: b.fecha_inicio, fecha_fin: b.fecha_fin,
      motivo: b.motivo ?? "", origen: b.origen ?? "manual",
    });
    if (error) return toast.error(error.message);
    toast.success("Bloqueo creado");
    setEditBloqueo(null); load();
  }
  async function cancelReserva(id: string) {
    if (!confirm("¿Cancelar esta reserva?")) return;
    const { error } = await supabase.from("reservas").update({ estado: "cancelada" }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Reserva cancelada"); setDetailReserva(null); load();
  }
  async function deleteBloqueo(id: string) {
    if (!confirm("¿Eliminar este bloqueo?")) return;
    const { error } = await supabase.from("bloqueos").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Bloqueo eliminado"); setDetailBloqueo(null); load();
  }

  const habName = (id: string) => habs.find((h) => h.id === id)?.nombre ?? "—";

  const gridTemplate = { gridTemplateColumns: `160px repeat(${rangeDays}, ${colW}px)` };

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => navigate({ search: (p: any) => ({ ...p, tab: v as "calendario" | "bloqueos" }) })}
      className="space-y-6"
    >
      <TabsList>
        <TabsTrigger value="calendario">Calendario</TabsTrigger>
        <TabsTrigger value="bloqueos">Bloqueos</TabsTrigger>
      </TabsList>

      <TabsContent value="calendario" className="space-y-4">
        {/* Controles */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => shift(-rangeDays)}><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="font-serif text-lg capitalize min-w-[230px] text-center">
              {format(inicio, "d MMM", { locale: es })} – {format(fin, "d MMM yyyy", { locale: es })}
            </h2>
            <Button variant="outline" size="icon" onClick={() => shift(rangeDays)}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={goToday}>Hoy</Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border bg-background p-0.5">
              {(["14","30","90"] as RangoKey[]).map((k) => (
                <button
                  key={k}
                  onClick={() => setRango(k)}
                  className={cn(
                    "px-3 py-1 text-xs rounded",
                    rango === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {k === "14" ? "2 sem" : k === "30" ? "1 mes" : "3 meses"}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => openNuevoBloqueo()}><Plus className="mr-1 h-3 w-3" />Bloqueo</Button>
            <Button size="sm" onClick={() => openNuevaReserva()}><Plus className="mr-1 h-3 w-3" />Reserva</Button>
          </div>
        </div>

        {/* Leyenda */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-primary" /> Confirmada</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-primary/40 border border-primary/60" /> Pendiente</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-muted-foreground/50" /> Bloqueada</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm border bg-background" /> Libre</span>
        </div>

        {habs.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Aún no hay habitaciones dadas de alta.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full">
                  {/* Cabecera días */}
                  <div className="grid border-b bg-muted/30" style={gridTemplate}>
                    <div className="sticky left-0 z-20 bg-muted/30 border-r px-3 py-2 text-xs font-medium text-muted-foreground">
                      Habitación
                    </div>
                    {days.map((d) => {
                      const isToday = isSameDay(d, today);
                      const wknd = isWeekend(d);
                      const isFirst = d.getDate() === 1;
                      return (
                        <div
                          key={d.toISOString()}
                          className={cn(
                            "border-r text-center text-[10px] leading-tight py-1.5",
                            wknd && "bg-muted/50",
                            isToday && "bg-primary/15",
                          )}
                        >
                          <div className="uppercase text-muted-foreground">
                            {format(d, "EEEEE", { locale: es })}
                          </div>
                          <div className={cn("font-medium", isToday && "text-primary")}>
                            {format(d, "d")}
                          </div>
                          {isFirst && rangeDays > 14 && (
                            <div className="text-[9px] uppercase text-muted-foreground">{format(d, "MMM", { locale: es })}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Fila resumen "Casa completa" */}
                  <div className="grid border-b" style={gridTemplate}>
                    <div className="sticky left-0 z-20 bg-background border-r px-3 py-2 text-xs font-medium">
                      Ocupación
                      <div className="text-[10px] text-muted-foreground">Casa completa</div>
                    </div>
                    {days.map((d) => {
                      const o = ocupacionCasa(d);
                      const wknd = isWeekend(d);
                      const isToday = isSameDay(d, today);
                      return (
                        <div
                          key={d.toISOString()}
                          className={cn(
                            "border-r h-9 flex items-center justify-center",
                            wknd && "bg-muted/30",
                            isToday && "bg-primary/10",
                          )}
                          title={o === "full" ? "Completa" : o === "partial" ? "Parcial" : "Libre"}
                        >
                          <span className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            o === "full" && "bg-destructive",
                            o === "partial" && "bg-amber-500",
                            o === "empty" && "bg-emerald-500/60",
                          )} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Filas por habitación */}
                  {habs.map((h) => {
                    const rs = reservasDe(h.id);
                    const bs = bloqueosDe(h.id);
                    return (
                      <div key={h.id} className="grid border-b relative" style={gridTemplate}>
                        <div className="sticky left-0 z-20 bg-background border-r px-3 py-3 text-sm font-medium truncate">
                          {h.nombre}
                        </div>
                        {/* Celdas de fondo (clickables) */}
                        {days.map((d) => {
                          const wknd = isWeekend(d);
                          const isToday = isSameDay(d, today);
                          return (
                            <CellPopover
                              key={d.toISOString()}
                              wknd={wknd}
                              isToday={isToday}
                              onReserva={() => openNuevaReserva(h.id, d)}
                              onBloqueo={() => openNuevoBloqueo(h.id, d)}
                              date={d}
                              hab={h.nombre}
                            />
                          );
                        })}
                        {/* Barras de reservas */}
                        {rs.map(({ r, colStart, span, clippedLeft, clippedRight }) => {
                          const pendiente = r.estado === "pendiente";
                          return (
                            <button
                              key={r.id}
                              onClick={() => setDetailReserva(r)}
                              className={cn(
                                "absolute top-1 bottom-1 z-10 truncate text-[11px] px-2 text-left font-medium",
                                "transition hover:brightness-110",
                                pendiente
                                  ? "bg-primary/40 text-foreground border border-primary/60"
                                  : "bg-primary text-primary-foreground",
                                clippedLeft ? "rounded-r-md" : "rounded-l-md",
                                clippedRight ? "rounded-l-md" : "rounded-r-md",
                                !clippedLeft && !clippedRight && "rounded-md",
                              )}
                              style={{
                                left: `calc(160px + ${colStart} * ${colW}px + 2px)`,
                                width: `calc(${span} * ${colW}px - 4px)`,
                              }}
                              title={`${r.nombre_huesped} · ${r.fecha_entrada} → ${r.fecha_salida}`}
                            >
                              {r.nombre_huesped}
                            </button>
                          );
                        })}
                        {/* Barras de bloqueos */}
                        {bs.map(({ b, colStart, span }) => (
                          <button
                            key={b.id}
                            onClick={() => setDetailBloqueo(b)}
                            className="absolute top-1 bottom-1 z-10 rounded-md bg-muted-foreground/40 text-[11px] px-2 text-foreground text-left truncate hover:brightness-110"
                            style={{
                              left: `calc(160px + ${colStart} * ${colW}px + 2px)`,
                              width: `calc(${span} * ${colW}px - 4px)`,
                              backgroundImage: "repeating-linear-gradient(45deg, transparent 0 4px, hsl(var(--muted-foreground)/0.15) 4px 8px)",
                            }}
                            title={`${b.motivo || "Bloqueada"} · ${b.fecha_inicio} → ${b.fecha_fin}`}
                          >
                            {b.motivo || "Bloqueada"}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="bloqueos">
        <BloqueosPanel />
      </TabsContent>

      {/* Sheet detalle reserva */}
      <Sheet open={!!detailReserva} onOpenChange={(o) => !o && setDetailReserva(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {detailReserva && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif">{detailReserva.nombre_huesped}</SheetTitle>
                <SheetDescription>{habName(detailReserva.habitacion_id)}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Estado</span>
                  <Badge variant={detailReserva.estado === "confirmada" ? "default" : "secondary"}>{detailReserva.estado}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="text-xs text-muted-foreground">Entrada</div><div>{detailReserva.fecha_entrada}</div></div>
                  <div><div className="text-xs text-muted-foreground">Salida</div><div>{detailReserva.fecha_salida}</div></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="text-xs text-muted-foreground">Huéspedes</div><div>{detailReserva.num_huespedes}</div></div>
                  <div><div className="text-xs text-muted-foreground">Noches</div><div>{differenceInCalendarDays(parseYmd(detailReserva.fecha_salida), parseYmd(detailReserva.fecha_entrada))}</div></div>
                </div>
                <div><div className="text-xs text-muted-foreground">Email</div><div>{detailReserva.email_huesped || "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Teléfono</div><div>{detailReserva.telefono_huesped || "—"}</div></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="text-xs text-muted-foreground">Total</div><div>{Number(detailReserva.precio_total).toFixed(2)} €</div></div>
                  <div><div className="text-xs text-muted-foreground">Señal</div><div>{Number(detailReserva.senal_pagada).toFixed(2)} €</div></div>
                </div>
                {detailReserva.notas && (
                  <div><div className="text-xs text-muted-foreground">Notas</div><div className="whitespace-pre-wrap">{detailReserva.notas}</div></div>
                )}
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => { openEditReserva(detailReserva); setDetailReserva(null); }}>
                    <Pencil className="mr-2 h-4 w-4" />Editar
                  </Button>
                  <Button variant="destructive" className="flex-1" onClick={() => cancelReserva(detailReserva.id)}>
                    Cancelar reserva
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Sheet detalle bloqueo */}
      <Sheet open={!!detailBloqueo} onOpenChange={(o) => !o && setDetailBloqueo(null)}>
        <SheetContent className="w-full sm:max-w-md">
          {detailBloqueo && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif">{detailBloqueo.motivo || "Bloqueo"}</SheetTitle>
                <SheetDescription>{habName(detailBloqueo.habitacion_id)}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="text-xs text-muted-foreground">Desde</div><div>{detailBloqueo.fecha_inicio}</div></div>
                  <div><div className="text-xs text-muted-foreground">Hasta</div><div>{detailBloqueo.fecha_fin}</div></div>
                </div>
                <div><div className="text-xs text-muted-foreground">Origen</div><Badge variant="secondary">{detailBloqueo.origen}</Badge></div>
                <Button variant="destructive" className="w-full mt-4" onClick={() => deleteBloqueo(detailBloqueo.id)}>
                  <Trash2 className="mr-2 h-4 w-4" />Eliminar bloqueo
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Dialog nueva/editar reserva */}
      <NuevaReservaDialog
        open={reservaOpen}
        onOpenChange={(v) => { setReservaOpen(v); if (!v) setEditReserva(null); }}
        habitaciones={habs}
        initial={editReserva}
        onSaved={() => load()}
      />

      {/* Dialog nuevo bloqueo */}
      <Dialog open={!!editBloqueo} onOpenChange={(o) => !o && setEditBloqueo(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">Nuevo bloqueo</DialogTitle></DialogHeader>
          {editBloqueo && (
            <div className="space-y-3">
              <div><Label>Habitación</Label>
                <Select value={editBloqueo.habitacion_id} onValueChange={(v) => setEditBloqueo({ ...editBloqueo, habitacion_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>{habs.map((h) => <SelectItem key={h.id} value={h.id}>{h.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Desde</Label><Input type="date" value={editBloqueo.fecha_inicio ?? ""} onChange={(e) => setEditBloqueo({ ...editBloqueo, fecha_inicio: e.target.value })} /></div>
                <div><Label>Hasta</Label><Input type="date" value={editBloqueo.fecha_fin ?? ""} onChange={(e) => setEditBloqueo({ ...editBloqueo, fecha_fin: e.target.value })} /></div>
              </div>
              <div><Label>Motivo</Label><Input value={editBloqueo.motivo ?? ""} onChange={(e) => setEditBloqueo({ ...editBloqueo, motivo: e.target.value })} placeholder="Mantenimiento, uso propio…" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditBloqueo(null)}>Cancelar</Button>
            <Button onClick={saveBloqueo}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}

function CellPopover({
  wknd, isToday, onReserva, onBloqueo, date, hab,
}: {
  wknd: boolean; isToday: boolean;
  onReserva: () => void; onBloqueo: () => void;
  date: Date; hab: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "h-12 border-r hover:bg-accent/40 transition",
            wknd && "bg-muted/30",
            isToday && "bg-primary/10",
          )}
          aria-label={`${hab} ${format(date, "d MMM", { locale: es })}`}
        />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="px-2 py-1 text-xs text-muted-foreground">
          {hab} · {format(date, "EEEE d MMM", { locale: es })}
        </div>
        <button onClick={onReserva} className="w-full text-left rounded px-2 py-1.5 text-sm hover:bg-accent">
          + Crear reserva aquí
        </button>
        <button onClick={onBloqueo} className="w-full text-left rounded px-2 py-1.5 text-sm hover:bg-accent">
          + Bloquear esta fecha
        </button>
      </PopoverContent>
    </Popover>
  );
}